import { MESApiService, type spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type { Shelf } from "../models/Shelf";
import type {
	ShelfMoveNode,
	ShelfMovePlan,
	ShelfMoveSummary,
} from "../models/ShelfMove";

const DOCS_DB = import.meta.env.VITE_DOCS_DB || "docs_cmms";

export const getShelves = async () => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	return await apiService.callV2(`[${DOCS_DB}].[DOCS].[GetTree]`, parameters);
};

export const upsertShelf = async (data: Shelf) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "TreeId", value: data.TreeId },
		{ name: "ParentTreeId", value: data.ParentTreeId },
		{ name: "Abbrev", value: data.Abbrev },
		{ name: "Title", value: data.Title },
		{ name: "UniqueKey", value: data.UniqueKey },
	];

	return await apiService.callV2(
		`[${DOCS_DB}].[DOCS].[UpsertTreeNode]`,
		parameters,
	);
};

export const deleteShelf = async (TreeId: number) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];
	parameters.push({ name: "TreeId", value: TreeId });

	return await apiService.callV2(
		`[${DOCS_DB}].[DOCS].[DeleteTreeNode]`,
		parameters,
	);
};

/**
 * What moving a shelf would do, or, with apply, the move itself. Both return the same plan. Only a
 * shelf or group without documents moves. On apply, expectedNewKey is the key the preview showed:
 * the move is refused if it would now end elsewhere.
 */
export const moveShelf = async (
	treeId: number,
	newParentTreeId: number,
	newCode: string | null,
	apply: boolean,
	expectedNewKey: string | null = null,
	signal?: AbortSignal,
): Promise<ShelfMovePlan> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "TreeId", value: treeId },
		{ name: "NewParentTreeId", value: newParentTreeId },
		{ name: "NewCode", value: newCode },
		{ name: "Apply", value: apply },
		{ name: "ExpectedNewKey", value: expectedNewKey },
	];

	const resp = await apiService.callV2(
		`[${DOCS_DB}].[DOCS].[MoveTreeNode]`,
		parameters,
		signal,
	);

	if (!resp.ok) {
		throw new Error(resp.message);
	}

	return {
		summary: get(
			resp,
			"data.tables[0].rows[0]",
			null,
		) as ShelfMoveSummary | null,
		nodes: get(resp, "data.tables[1].rows", []) as ShelfMoveNode[],
	};
};
